import React, { useId, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { brandInputFocus } from '../brandChrome';
import { dashboardPanelFeatured } from './dashboardStyles';
import { DashboardIcon } from './DashboardIcon';
import { DOCUMENTATION_GLOSSARY } from '../../data/documentationGlossary';

/** @deprecated Prefer DOCUMENTATION_GLOSSARY — kept for existing Help dialog imports. */
export const CRYPTO_GLOSSARY = DOCUMENTATION_GLOSSARY;

const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

type Props = {
    defaultExpanded?: boolean;
};

type ContentProps = {
    listClassName?: string;
};

/** Shared searchable glossary body used by the global Help dialog. */
export const CryptoFaqContent: React.FC<ContentProps> = ({ listClassName = 'max-h-64' }) => {
    const [search, setSearch] = useState('');

    const q = search.trim().toLowerCase();
    const entries = Object.entries(DOCUMENTATION_GLOSSARY).filter(
        ([term, def]) => !q || term.toLowerCase().includes(q) || def.toLowerCase().includes(q),
    );

    return (
        <div className="space-y-2">
            <input
                type="text"
                placeholder="Search terms..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search crypto FAQ"
                className={`w-full text-sm px-2.5 py-1.5 bg-secondary border border-border rounded-md ${brandInputFocus}`}
            />
            <div className={clsx('overflow-y-auto space-y-1', listClassName)}>
                {entries.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-1">No matching terms.</p>
                ) : (
                    entries.map(([term, definition]) => (
                        <details key={term} className="group">
                            <summary className="text-sm font-medium text-foreground cursor-pointer hover:text-primary transition-colors px-2 py-1 rounded hover:bg-secondary/50">
                                {term}
                            </summary>
                            <p className="text-xs text-muted-foreground leading-relaxed px-2 pb-1.5 pl-4">
                                {definition}
                            </p>
                        </details>
                    ))
                )}
            </div>
        </div>
    );
};

export const CryptoFaqSection: React.FC<Props> = ({ defaultExpanded = false }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const panelId = useId();

    return (
        <div className={clsx('overflow-hidden', dashboardPanelFeatured)}>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-controls={panelId}
                aria-label={expanded ? 'Collapse crypto FAQ' : 'Expand crypto FAQ'}
                className={clsx(
                    'w-full flex items-center justify-between gap-3 px-4 py-3.5 min-h-11 text-left',
                    'hover:bg-brand-cream/40 dark:hover:bg-primary/5 active:bg-muted/50 transition-colors',
                    focusRing,
                )}
            >
                <div className="flex items-start gap-2.5 min-w-0">
                    <span className="mt-0.5 p-1.5 rounded-md bg-primary/10 shrink-0">
                        <HelpCircle className="w-3.5 h-3.5 text-primary" aria-hidden />
                    </span>
                    <div className="min-w-0">
                        <h3 className="font-semibold text-sm text-foreground">Crypto FAQ</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Glossary of investigation terms — open when you need a definition
                        </p>
                    </div>
                </div>
                <DashboardIcon
                    variant="inline"
                    tone="neutral"
                    icon={ChevronDown}
                    className={clsx(
                        'transition-transform duration-200 ease-out shrink-0',
                        expanded ? 'rotate-0' : '-rotate-90',
                    )}
                />
            </button>
            <div
                id={panelId}
                className={clsx(
                    'grid transition-[grid-template-rows] duration-200 ease-out',
                    expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
            >
                <div className="overflow-hidden">
                    <div className="px-4 pb-4 border-t border-border/60 pt-3 bg-brand-cream/20 dark:bg-primary/[0.03]">
                        <CryptoFaqContent />
                    </div>
                </div>
            </div>
        </div>
    );
};
